import { ActionIcon, Card, Flex, Title } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "../Modal";
import Menu from "./components/Menu/Menu";

import {
  IconArrowLeft,
  IconLock,
  IconLogout,
  IconMapPin,
  IconPhone,
  IconUser,
} from "@/components/icons";
import {
  UserProfileTabAddress,
  UserProfileTabContacts,
  UserProfileTabPersonalInfo,
  UserProfileTabSecurity,
} from "@/components/widgets/UserProfile/components";

import type { FC } from "react";
import type { TabType } from "./interfaces";

interface ProfileModalProps {
  opened: boolean;
  onClose: () => void;
}
const ProfileModal: FC<ProfileModalProps> = ({ opened, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>(null);

  const navigationItems = [
    { label: "personal-info", icon: IconUser },
    { label: "contacts", icon: IconPhone },
    { label: "address", icon: IconMapPin },
    { label: "security", icon: IconLock },
    { label: "logout", icon: IconLogout },
  ];

  const title = activeTab === null ? t("profile.profile") : t("profile.navLinks." + activeTab);
  const titleBreadcrumbs =
    activeTab === null
      ? t("profile.profile")
      : `${t("profile.profile")} / ${t("profile.navLinks." + activeTab)}`;

  /* Handlers */
  const tabSelectionHandler = (tab: TabType) => {
    setActiveTab(tab);
  };

  const tabNavigationHandler = (tab: TabType) => {
    if (tab === null) {
      onClose();
    } else {
      setActiveTab(null);
    }
  };

  const renderActiveSection = () => {
    switch (activeTab) {
      case "personal-info":
        return (
          <Card>
            <UserProfileTabPersonalInfo />
          </Card>
        );
      case "contacts":
        return (
          <Card>
            <UserProfileTabContacts />
          </Card>
        );
      case "address":
        return <UserProfileTabAddress />;
      case "security":
        return (
          <Card>
            <UserProfileTabSecurity />
          </Card>
        );
      default:
        return (
          <Card>
            <Menu navigationItems={navigationItems} tabSelectionHandler={tabSelectionHandler} />
          </Card>
        );
    }
  };

  return (
    <Modal
      title={
        <Flex align="center" justify="center" gap="lg" pb="sm" w="100%">
          <ActionIcon onClick={() => tabNavigationHandler(activeTab)}>
            <IconArrowLeft />
          </ActionIcon>
          <Title fw="normal" fz="lg" ta="center" w="100%">
            {titleBreadcrumbs}
          </Title>
        </Flex>
      }
      hideCloseButton
      opened={opened}
      fullScreen
      onClose={onClose}
    >
      {/* Title section */}
      <Title fw="normal" pb="lg" px="xs" fz="1.875em">
        {title}
      </Title>

      {/* Content */}
      {renderActiveSection()}
    </Modal>
  );
};

export default ProfileModal;
